/*
  role-based simplified UI (demo build)
  each role exposes only the subset of features its job needs : the left menu is filtered,
  the routes of the hidden features are denied, and the thematic tab rows of the big
  workspaces (dashboard, supervision, gestion, planification) are narrowed down.
  'admin' keeps the historical, complete UI — it is the default and the only role used on
  ACTV builds, so nothing changes for them.
*/

// identifier of a role, persisted in localStorage by the role service
export type AppRoleId = 'admin' | 'exploitation' | 'finance' | 'planification' | 'relation'

// a page owning a thematic tab row that a role can narrow down
export type AppRolePageKey = 'overview' | 'supervision' | 'gestion' | 'planning'

// wildcard accepted in navLabels / themeKeys, meaning "every entry of that list"
export const kRoleAll = '*'

// describes one role : what it sees in the menu and inside the thematic pages
export interface AppRoleProfile {
  id: AppRoleId // role id, matches the value stored in localStorage
  label: string // dictionary key, translated through kRoleDictionary
  literalLabel: string // French label used when the dictionary has no entry
  icon: string // selector icon (must exist in assets/icons/icons.svg)
  homePath: string // landing page, and fallback used when a denied route is opened
  navLabels: string[] // NavLink labels kept in the menu (kRoleAll = keep them all)
  themeKeys: Record<AppRolePageKey, string[]> // thematic tabs kept per page (kRoleAll = keep them all)
}

// route prefixes owned by each nav label, used to allow / deny a URL for the active role.
// a URL matching no entry (login, 404, internal pages) stays allowed for every role.
// note : the longest matching prefix wins, so '/supervision/wall' is checked against
// 'supervision_wall' and not against 'supervision'.
export const kRoutesByNavLabel: Record<string, string[]> = {
  dashboard: ['/overview'],
  supervision: ['/supervision'],
  supervision_wall: ['/supervision/wall'],
  gestion: ['/gestion'],
  planning: ['/planning'],
  mobility: ['/mobility'],
  usager: ['/usager'],
  lost_n_found: ['/lost-found'],
  social: ['/social'],
  conformite: ['/conformite'],
  resilience: ['/resilience'],
  surete: ['/surete'],
  financement: ['/financement'],
  activities: ['/activities'],
  vaporetto_terminals: ['/terminals-nav'],
  bus_terminals: ['/terminals-bus'],
  validations: ['/validations'],
  reseller_sales: ['/top-up'],
  palmari_sales: ['/pax'],
  offer: ['/offer'],
  resellers: ['/resellers'],
  agents: ['/agents'],
  parameters: ['/parameters'],
  alerts: ['/alerts'],
  fines: ['/fines'],
  settings: ['/settings'],
  logs: ['/logs'],
  users: ['/users'],
}

/*
  the role table — the single place to edit when a feature must move from one job to another.
  'billettique' is never listed : it is the ticketing hub entry, kept automatically as soon as
  one of its sub-tabs (validations, reseller_sales, …) is allowed.
*/
export const kRoleProfiles: AppRoleProfile[] = [
  // complete UI : every menu entry and every thematic tab, i.e. the application as it was
  {
    id: 'admin',
    label: 'role_admin',
    literalLabel: 'Administrateur',
    icon: 'cog',
    homePath: '/overview',
    navLabels: [kRoleAll],
    themeKeys: {
      overview: [kRoleAll],
      supervision: [kRoleAll],
      gestion: [kRoleAll],
      planning: [kRoleAll],
    },
  },

  // real-time operation of the network : supervision, equipment, incidents, crisis, field safety
  {
    id: 'exploitation',
    label: 'role_exploitation',
    literalLabel: 'Exploitation',
    icon: 'eye',
    homePath: '/overview',
    navLabels: [
      'dashboard',
      'supervision',
      'supervision_wall',
      'surete',
      'resilience',
      'activities',
      'vaporetto_terminals',
      'bus_terminals',
      'validations',
      'alerts',
      'settings',
      'logs',
    ],
    themeKeys: {
      // the whole city map except the ZFE / air-quality theme, which belongs to planning
      overview: ['all', 'bus', 'metro', 'boat', 'carpark', 'bike', 'freefloat', 'ev', 'works', 'access', 'safety', 'flows', 'coverage', 'zfectrl'],
      supervision: [kRoleAll],
      gestion: [],
      planning: [],
    },
  },

  // revenue, contracts, governance : steering the money side of the network
  {
    id: 'finance',
    label: 'role_finance',
    literalLabel: 'Finances',
    icon: 'cash-multiple',
    homePath: '/overview',
    navLabels: [
      'dashboard',
      'gestion',
      'financement',
      'validations',
      'reseller_sales',
      'palmari_sales',
      'offer',
      'resellers',
      'agents',
      'parameters',
      'alerts',
      'fines',
      'settings',
      'logs',
    ],
    themeKeys: {
      overview: ['all', 'flows'],
      supervision: [],
      gestion: ['all', 'decisions', 'billettique', 'finances', 'patrimoine', 'dsp', 'commercial'],
      planning: [],
    },
  },

  // prospective : demand, network capacity, fares, environment, open data
  {
    id: 'planification',
    label: 'role_planification',
    literalLabel: 'Planification',
    icon: 'magnify',
    homePath: '/overview',
    navLabels: [
      'dashboard',
      'planning',
      'gestion',
      'conformite',
      'settings',
    ],
    themeKeys: {
      overview: ['all', 'flows', 'coverage', 'air', 'access', 'works'],
      supervision: [],
      // the forward-looking panels of the management workspace only
      gestion: ['all', 'strategie', 'environnement', 'data', 'mobilites'],
      planning: [kRoleAll],
    },
  },

  // customer relationship : mobility accounts, social fares, claims, lost & found
  {
    id: 'relation',
    label: 'role_relation',
    literalLabel: 'Relation client',
    icon: 'account-circle',
    homePath: '/overview',
    navLabels: [
      'dashboard',
      'mobility',
      'usager',
      'lost_n_found',
      'social',
      'gestion',
      'settings',
    ],
    themeKeys: {
      overview: ['all', 'access', 'coverage'],
      supervision: [],
      // the citizen-facing panels of the management workspace only
      gestion: ['all', 'citoyen', 'commercial'],
      planning: [],
    },
  },
]

// the default role — the complete UI, so an unknown / missing stored value changes nothing
export const kDefaultRoleId: AppRoleId = 'admin'

// the profile of a role id, falling back to the complete UI when the id is unknown
export function ROLE_PROFILE_BY_ID (inId: string): AppRoleProfile {
  const vProfile = kRoleProfiles.find(_ => _.id === inId)
  return vProfile ?? kRoleProfiles[0]
}
